export async function encryptChunk(
  plaintext: Uint8Array,
  key: Uint8Array,
): Promise<Uint8Array> {
  const buffer = plaintext.buffer.slice(
    plaintext.byteOffset,
    plaintext.byteOffset + plaintext.byteLength,
  );
  const blob = await windowCryptoEncrypt(buffer, key);
  return new Uint8Array(await blob.arrayBuffer());
}

export async function decryptChunk(
  encryptedChunk: Uint8Array,
  key: Uint8Array,
): Promise<Uint8Array> {
  const buffer = encryptedChunk.buffer.slice(
    encryptedChunk.byteOffset,
    encryptedChunk.byteOffset + encryptedChunk.byteLength,
  );
  const result = await windowCryptoDecrypt(buffer, key);
  if (!result) {
    throw new Error("Chunk decryption failed");
  }
  return new Uint8Array(result);
}

export async function windowCryptoDecrypt(
  ciphertext: ArrayBuffer,
  key: Uint8Array,
): Promise<ArrayBuffer | undefined> {
  /** @type {AesGcmParams} */
  const params: AesGcmParams = {
    name: "AES-GCM",
    iv: new Uint8Array(ciphertext, 0, 12),
  };

  try {
    const crypk = await window.crypto.subtle.importKey(
      "raw",
      key,
      params.name,
      false,
      ["decrypt"],
    );

    return await window.crypto.subtle.decrypt(
      params,
      crypk,
      new Uint8Array(ciphertext, 12),
    );
  } catch (error) {
    if (error instanceof Error) {
      // eslint-disable-next-line no-console
      console.debug(`Decryption failed: ${error.name} - ${error.message}`, {
        ciphertextLength: ciphertext.byteLength,
      });
    }
    return undefined;
  }
}

export async function windowCryptoEncrypt(
  plaintext: ArrayBuffer,
  key: Uint8Array,
): Promise<Blob> {
  const nonce = window.crypto.getRandomValues(new Uint8Array(12));

  /** @type {AesGcmParams} */
  const params: AesGcmParams = {
    name: "AES-GCM",
    iv: nonce,
  };
  const crypk = await window.crypto.subtle.importKey(
    "raw",
    key,
    params.name,
    false,
    ["encrypt"],
  );
  return new Blob(
    [
      nonce,
      new Uint8Array(
        await window.crypto.subtle.encrypt(params, crypk, plaintext),
      ),
    ],
    { type: "application/octet-stream" },
  );
}
